import React, { useState, type JSX } from 'react';

import SquarePenIcon from 'assets/icons/square-pen.inline.svg';
import PanelLeftIcon from 'assets/icons/panel-left.inline.svg';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SidePeekIcon from 'assets/icons/side-peek.inline.svg';
import FloatingPeekIcon from 'assets/icons/floating-peek.inline.svg';
import CenterPeekIcon from 'assets/icons/center-peek.inline.svg';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import { tracker } from 'app-components/utils/core';
import { useChatSession } from 'components/ChatLauncher/chatSession';
import type { ChatViewMode } from 'components/ChatLauncher/ChatDockContext';

type ViewModeIcon = React.FC<React.SVGProps<SVGSVGElement>>;

const VIEW_MODE_OPTIONS: { mode: ChatViewMode; label: string; Icon: ViewModeIcon }[] = [
  {
    mode: 'floating',
    label: 'Floating',
    Icon: FloatingPeekIcon,
  },
  {
    mode: 'docked',
    label: 'Split view',
    Icon: SidePeekIcon,
  },
  {
    mode: 'expanded',
    label: 'Full screen',
    Icon: CenterPeekIcon,
  },
];

type ChatHeaderProps = {
  onClose: () => void;
  handleMinimize: () => void;
  onViewModeChange: (mode: ChatViewMode) => void;
  viewMode: ChatViewMode;
  setShowSideBarMenu: React.Dispatch<React.SetStateAction<boolean>>;
  showSideBarMenu: boolean;
  handleRef: React.RefObject<HTMLDivElement | null>;
  isDefaultState?: boolean;
};

const ChatHeader = ({
  onClose,
  handleMinimize,
  onViewModeChange,
  viewMode,
  setShowSideBarMenu,
  showSideBarMenu,
  handleRef,
  isDefaultState,
}: ChatHeaderProps): JSX.Element => {
  const { startNewChat } = useChatSession();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';
  const isFloating = viewMode === 'floating';
  const hideDrawerControls = viewMode === 'expanded' && showSideBarMenu;

  const handleNewConversation = async () => {
    tracker.track(trackingEvents.AIChatNew);
    tracker.track(trackingEvents.AIChatSaved);
    await startNewChat();
  };

  const handleOpenSidebar = () => {
    setShowSideBarMenu((prev) => !prev);
  };

  const handleClose = () => {
    tracker.track(trackingEvents.AIChatClosed);
    tracker.track(trackingEvents.AIChatSaved);
    onClose();
  };

  const handleOpenViewModeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseViewModeMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleSelectViewMode = (mode: ChatViewMode) => {
    onViewModeChange(mode);
    handleCloseViewModeMenu();
  };

  const theme = useTheme();
  const ActiveViewModeIcon = VIEW_MODE_OPTIONS.find((option) => option.mode === viewMode)?.Icon ?? FloatingPeekIcon;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(to bottom, ${theme.palette.background.paper} 80%, transparent)`,
          zIndex: 0,
          userSelect: 'none',
        }}
      />
      <Stack
        direction='row'
        alignItems='center'
        padding={2}
        ref={handleRef}
        sx={{
          position: 'relative',
          zIndex: 10,
          cursor: isFloating ? 'grab' : 'default',
          gap: 1,
        }}
      >
        {!hideDrawerControls && (
          <Stack gap={1} direction='row' alignItems='center' sx={{ flexShrink: 0 }}>
            <Tooltip title='Open Sidebar'>
              <IconButton
                id='ai-chat-header-sidebar'
                variant='tonal'
                size='small'
                onClick={handleOpenSidebar}
                sx={{ cursor: 'w-resize' }}
              >
                <PanelLeftIcon width={18} height={18} />
              </IconButton>
            </Tooltip>
            {!isDefaultState && (
              <Tooltip title='New Chat'>
                <Button
                  id='ai-chat-header-new'
                  variant='outlined'
                  size='small'
                  onClick={handleNewConversation}
                  color='primary'
                  startIcon={<SquarePenIcon width={18} height={18} />}
                >
                  New
                </Button>
              </Tooltip>
            )}
            <Tooltip title='Quicken AI is currently in beta and may make mistakes. Please double-check your results.'>
              <Chip label='Beta' variant='tonal' color='warning' size='small' sx={{ cursor: 'pointer' }} />
            </Tooltip>
          </Stack>
        )}

        <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }} />

        <Stack gap={0.5} direction='row' alignItems='center' justifyContent='flex-end' sx={{ flexShrink: 0 }}>
          <Tooltip title={`Switch view mode (${cmdKey}.)`}>
            <IconButton
              id='ai-chat-header-split-view'
              variant='tonal'
              size='small'
              onClick={handleOpenViewModeMenu}
              color={viewMode !== 'floating' ? 'primary' : 'default'}
              aria-haspopup='true'
              aria-expanded={Boolean(menuAnchorEl)}
            >
              <ActiveViewModeIcon width={20} height={20} />
            </IconButton>
          </Tooltip>
          <Menu
            id='ai-chat-header-view-mode-menu'
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleCloseViewModeMenu}
          >
            {VIEW_MODE_OPTIONS.map(({ mode, label, Icon }) => {
              const isSelected = viewMode === mode;

              return (
                <MenuItem
                  key={mode}
                  id={`ai-chat-header-view-mode-${mode}`}
                  selected={isSelected}
                  onClick={() => handleSelectViewMode(mode)}
                  sx={{
                    paddingX: 1,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isSelected ? 'primary.main' : 'text.primary',
                    }}
                  >
                    <Icon width={20} height={20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    slotProps={{
                      primary: {
                        variant: 'paragraphRegular',
                        sx: { color: isSelected ? 'primary.main' : 'text.primary' },
                      },
                    }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
          <Tooltip title='Minimize'>
            <IconButton id='ai-chat-header-minimize' variant='tonal' size='small' onClick={handleMinimize}>
              <RemoveRoundedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Close'>
            <IconButton id='ai-chat-header-close' variant='tonal' size='small' onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ChatHeader;
